import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './NoteAddCN.css'
import { useDropzone } from 'react-dropzone'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { ImagePlus, Images, Laptop, StickyNote, X } from 'lucide-react'
import useMediaQuery from 'hooks/useMediaQuery'
import ImageLibraryPicker from 'components/ImageLibraryPicker/ImageLibraryPicker'
import { buildQuillModules, QUILL_FORMATS } from './quillConfig'
import { extractNoteImageUrls, stripImagesFromNoteHtml } from 'utils/noteImages'
import type { LibraryImage } from 'utils/libraryImages'

/** How the attached images should be turned into notes. */
export type ComposerNotePlan = 'single' | 'per-image'

interface NoteAddCNProps {
	open: boolean
	onClose: () => void
	/**
	 * `contents` is the note bodies to create, in order — one entry normally,
	 * one per image when the author chose to split them.
	 *
	 * `content` is the first entry, kept so any caller not yet reading
	 * `contents` still creates a note rather than breaking.
	 */
	onSubmit: (note: {
		content: string
		contents: string[]
		parentid: string | null
		/**
		 * Images taken from the library rather than the device. They are already
		 * on Cloudinary, so they are named here instead of being re-uploaded: the
		 * caller writes a row for each against its own record.
		 */
		libraryImages: LibraryImage[]
	}) => void
	parentid?: string | null
}

/**
 * One image waiting in the tray.
 *
 * A device file and a library image live in the same list so they share one
 * preview grid, one removal path and one place in the running order. What they
 * do not share is submit: a file becomes base64 and is uploaded, a library
 * image is already hosted and only cites its URL.
 */
type AttachedImage =
	| { id: string; kind: 'file'; file: File; preview: string }
	| { id: string; kind: 'library'; image: LibraryImage; preview: string }

type LibraryAttachedImage = Extract<AttachedImage, { kind: 'library' }>

const isLibraryAttachedImage = (image: AttachedImage): image is LibraryAttachedImage =>
	image.kind === 'library'

/** One image that will become its own note, with the caption written for it. */
interface SplitTarget {
	id: string
	preview: string
	/** Present for tray attachments; inline embeds already carry their own src. */
	file?: File
	/** Set for images already embedded in the body. */
	inlineSrc?: string
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`

const readAsDataUrl = (file: File) =>
	new Promise<string>(resolve => {
		const reader = new FileReader()
		reader.onload = e => resolve(e.target?.result as string)
		reader.readAsDataURL(file)
	})

const NoteAddCN: React.FC<NoteAddCNProps> = ({ open, onClose, onSubmit, parentid = null }) => {
	const [editorHtml, setEditorHtml] = useState('')
	const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([])
	const [notePlan, setNotePlan] = useState<ComposerNotePlan>('single')
	const [captions, setCaptions] = useState<Record<string, string>>({})
	const [sourceChooserOpen, setSourceChooserOpen] = useState(false)
	const [libraryOpen, setLibraryOpen] = useState(false)
	const quillRef = useRef<ReactQuill>(null)
	const isMobile = useMediaQuery('only screen and (max-width: 47.9375rem)')

	// Cleanup object URLs on unmount. Only a device file has one; a library
	// image's preview is a Cloudinary URL and revoking it would do nothing.
	useEffect(() => {
		return () => {
			attachedImages.forEach(img => {
				if (img.kind === 'file') URL.revokeObjectURL(img.preview)
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Stable, so the memo below does not rebuild the toolbar on every keystroke.
	const openSourceChooser = useCallback(() => setSourceChooserOpen(true), [])

	// Toolbar config lives in ./quillConfig so the global Quill icon registry is
	// mutated exactly once, no matter how many composers are mounted.
	const modules = useMemo(
		() => buildQuillModules({ isMobile, quillRef, onChooseImageSource: openSourceChooser }),
		[isMobile, openSourceChooser],
	)

	// --- Image attachment via dropzone ---

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const newImages = acceptedFiles.map(
			file =>
				({
					id: newId(),
					kind: 'file',
					file,
					preview: URL.createObjectURL(file),
				}) as AttachedImage,
		)
		setAttachedImages(prev => [...prev, ...newImages])
	}, [])

	/*
		`noClick` because the click now belongs to the source chooser: dropping a
		file on the tray is unambiguous, but clicking it is the moment the author
		has to be asked device or library. `open()` is what the chooser's device
		option calls, so the file input itself is unchanged.
	*/
	const { getRootProps, getInputProps, isDragActive, open: openDeviceFiles } = useDropzone({
		onDrop,
		accept: { 'image/*': [] },
		noClick: true,
		noKeyboard: true,
	})

	const addLibraryImages = useCallback((images: LibraryImage[]) => {
		setAttachedImages(prev => {
			// The same picture twice in one note is never the intent, and the
			// library can be reached from both the toolbar and the tray.
			const have = new Set(prev.filter(isLibraryAttachedImage).map(img => img.image.url))
			const added = images
				.filter(image => !have.has(image.url))
				.map(image => ({ id: newId(), kind: 'library', image, preview: image.url }) as AttachedImage)
			return [...prev, ...added]
		})
	}, [])

	const removeAttachedImage = (id: string) => {
		setAttachedImages(prev => {
			const removed = prev.find(img => img.id === id)
			if (removed?.kind === 'file') URL.revokeObjectURL(removed.preview)
			return prev.filter(img => img.id !== id)
		})
	}

	// --- Splitting into one note per image ---

	/**
	 * Every image that will end up in the note: those the author embedded in the
	 * body first, then the tray attachments, matching the order they are written
	 * out on submit.
	 */
	const splitTargets = useMemo<SplitTarget[]>(() => {
		const inline = extractNoteImageUrls(editorHtml).map((src, index) => ({
			id: `inline-${index}`,
			preview: src,
			inlineSrc: src,
		}))
		const attached = attachedImages.map(img => ({
			id: img.id,
			preview: img.preview,
			file: img.kind === 'file' ? img.file : undefined,
		}))
		return [...inline, ...attached]
	}, [editorHtml, attachedImages])

	// Splitting a reply into several replies is rarely what anyone means, so the
	// choice is offered only on a top-level note carrying more than one image.
	const canSplit = !parentid && splitTargets.length > 1
	const isSplitting = canSplit && notePlan === 'per-image'

	// A plan only makes sense while there are images to split.
	useEffect(() => {
		if (!canSplit && notePlan === 'per-image') setNotePlan('single')
	}, [canSplit, notePlan])

	const choosePlan = (plan: ComposerNotePlan) => {
		if (plan === 'per-image') {
			// Carry the body across so text already written is not lost — each
			// caption starts from it and can be edited down.
			const seed = stripImagesFromNoteHtml(editorHtml)
				.replace(/<[^>]*>/g, ' ')
				.replace(/\s+/g, ' ')
				.trim()
			setCaptions(prev => {
				const next = { ...prev }
				splitTargets.forEach(target => {
					if (next[target.id] === undefined) next[target.id] = seed
				})
				return next
			})
		}
		setNotePlan(plan)
	}

	// --- Submit ---

	const buildContents = async (): Promise<string[]> => {
		/*
			What each tray image contributes to the note body.

			A device file becomes base64, which `convertRTEImages` finds at submit
			and swaps for a Cloudinary URL. A library image is already hosted, so
			its own URL goes straight in. That asymmetry is the whole point: the
			extractor matches `data:image` and nothing else, so a library image
			passes through the upload path untouched.
		*/
		const attachedSrc = await Promise.all(
			attachedImages.map(img => (img.kind === 'file' ? readAsDataUrl(img.file) : img.image.url)),
		)

		if (!isSplitting) {
			const imgTags = attachedSrc.map(src => `<p><img src="${src}"></p>`).join('')
			return [`${editorHtml}${imgTags}`]
		}

		// One note per image. The shared body is replaced by the per-image
		// captions, and images already embedded in the body are pulled out into
		// their own notes too, so "a separate note per image" means every image
		// rather than only the tray ones.
		const srcById = new Map<string, string>()
		splitTargets.forEach(target => {
			if (target.inlineSrc) srcById.set(target.id, target.inlineSrc)
		})
		attachedImages.forEach((img, index) => srcById.set(img.id, attachedSrc[index]))

		return splitTargets.map(target => {
			const caption = (captions[target.id] || '').trim()
			const body = caption ? `<p>${caption}</p>` : ''
			return `${body}<p><img src="${srcById.get(target.id)}"></p>`
		})
	}

	const handleSubmit = async () => {
		const contents = (await buildContents()).filter(html => {
			const text = html.replace(/<[^>]*>/g, '').trim()
			return text !== '' || html.includes('<img')
		})

		if (contents.length === 0) return

		const libraryImages = attachedImages
			.filter(isLibraryAttachedImage)
			.map(img => img.image)

		onSubmit({ content: contents[0], contents, parentid: parentid || null, libraryImages })
		resetState()
	}

	const resetState = () => {
		setEditorHtml('')
		attachedImages.forEach(img => {
			if (img.kind === 'file') URL.revokeObjectURL(img.preview)
		})
		setAttachedImages([])
		setSourceChooserOpen(false)
		setLibraryOpen(false)
		setNotePlan('single')
		setCaptions({})
	}

	const handleClose = () => {
		resetState()
		onClose()
	}

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogContent className="note-add-cn max-w-2xl p-0">
				<DialogHeader className="px-4 pt-4 pb-0">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<StickyNote className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Add Note</DialogTitle>
							<DialogDescription>
								{isSplitting
									? `Creating ${splitTargets.length} notes, one per image`
									: 'Add a note to the activity feed'}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="max-h-[60vh] overflow-y-auto">
					{/* Editor. Hidden while splitting: the per-image captions replace
					    the shared body, and leaving it on screen would imply text
					    that is not going to be used. */}
					{!isSplitting && (
						<div className="p-4 pb-0">
							<ReactQuill
								ref={quillRef}
								theme="snow"
								placeholder="Add a note..."
								value={editorHtml}
								onChange={setEditorHtml}
								modules={modules}
								formats={QUILL_FORMATS}
							/>
						</div>
					)}

					{canSplit && (
						<div className="px-4 pt-4">
							<div
								role="radiogroup"
								aria-label="How to post these images"
								className="flex flex-wrap gap-2"
							>
								{(
									[
										['single', 'One note for all images'],
										['per-image', 'A separate note per image'],
									] as [ComposerNotePlan, string][]
								).map(([plan, label]) => (
									<button
										key={plan}
										type="button"
										role="radio"
										aria-checked={notePlan === plan}
										onClick={() => choosePlan(plan)}
										className={cn(
											'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
											notePlan === plan
												? 'border-primary bg-primary/10 text-primary'
												: 'border-border bg-white text-gray-600 hover:border-primary/50',
										)}
									>
										{label}
									</button>
								))}
							</div>
						</div>
					)}

					{isSplitting && (
						<div className="space-y-3 p-4">
							<p className="text-xs text-muted-foreground">
								Each image becomes its own note. Leave a caption blank to post that
								image without text.
							</p>
							{splitTargets.map((target, index) => (
								<div
									key={target.id}
									className="rounded-lg border border-border p-3 sm:flex sm:items-start sm:gap-3"
								>
									<img
										src={target.preview}
										alt=""
										className="mb-2 h-20 w-20 shrink-0 rounded-md border border-border object-cover sm:mb-0"
									/>
									<div className="min-w-0 flex-1">
										<label
											className="mb-1 block text-xs text-muted-foreground"
											htmlFor={`note-caption-${target.id}`}
										>
											Note for image {index + 1}
										</label>
										<textarea
											id={`note-caption-${target.id}`}
											value={captions[target.id] ?? ''}
											onChange={e =>
												setCaptions(prev => ({ ...prev, [target.id]: e.target.value }))
											}
											rows={2}
											className="w-full rounded-md border border-border px-2 py-1.5 text-sm"
											placeholder="Add a note for this image..."
										/>
									</div>
								</div>
							))}
						</div>
					)}

					{/* Attached images preview grid */}
					{!isSplitting && attachedImages.length > 0 && (
						<div className="px-4 pt-3">
							<div className="grid grid-cols-4 gap-2">
								{attachedImages.map(img => (
									<div
										key={img.id}
										className="relative group rounded-md overflow-hidden border border-border"
									>
										<img src={img.preview} alt="" className="w-full h-20 object-cover" />
										{img.kind === 'library' && (
											<span
												title="From your image library"
												className="absolute bottom-1 left-1 inline-flex items-center rounded bg-black/60 px-1 py-0.5 text-white"
											>
												<Images className="h-3 w-3" />
												<span className="sr-only">From your image library</span>
											</span>
										)}
										<button
											type="button"
											onClick={() => removeAttachedImage(img.id)}
											aria-label="Remove image"
											className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
										>
											<X className="w-3 h-3" />
										</button>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Bottom bar: dropzone trigger + Post button */}
				<div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 mt-2">
					{/* Still a drop target: dropping a file is unambiguous. Clicking is
					    what now asks where the picture is coming from. */}
					<div
						{...getRootProps()}
						className={cn(
							'rounded-md border border-dashed transition-colors',
							isDragActive
								? 'border-primary bg-primary/5 text-primary'
								: 'border-gray-300 text-gray-500',
						)}
					>
						<input {...getInputProps()} />
						<button
							type="button"
							onClick={openSourceChooser}
							className="flex items-center gap-2 px-3 py-1.5 text-sm hover:text-primary"
						>
							<ImagePlus className="w-4 h-4" />
							<span>Add images</span>
						</button>
					</div>

					<Button variant="primary" onClick={handleSubmit}>
						{isSplitting ? `Post ${splitTargets.length} notes` : 'Post'}
					</Button>
				</div>
			</DialogContent>

			{/* Where is this picture coming from? Two answers, so neither is buried
			    behind the other. The library option is the one that costs nothing:
			    the file is already uploaded. */}
			<Dialog open={sourceChooserOpen} onClose={() => setSourceChooserOpen(false)}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Add images</DialogTitle>
						<DialogDescription>Choose where these images come from.</DialogDescription>
					</DialogHeader>
					<div className="grid gap-2 p-5 pt-2">
						<button
							type="button"
							onClick={() => {
								setSourceChooserOpen(false)
								openDeviceFiles()
							}}
							className="flex items-start gap-3 rounded-lg border border-border p-3 text-left hover:border-primary hover:bg-primary/5"
						>
							<Laptop className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
							<span>
								<span className="block text-sm font-medium text-gray-900">
									From this device
								</span>
								<span className="block text-sm text-gray-500">
									Pick one or more files to upload.
								</span>
							</span>
						</button>
						<button
							type="button"
							onClick={() => {
								setSourceChooserOpen(false)
								setLibraryOpen(true)
							}}
							className="flex items-start gap-3 rounded-lg border border-border p-3 text-left hover:border-primary hover:bg-primary/5"
						>
							<Images className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
							<span>
								<span className="block text-sm font-medium text-gray-900">
									From the image library
								</span>
								<span className="block text-sm text-gray-500">
									Everything you have already, including photographs taken in the field.
								</span>
							</span>
						</button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Mounted only while open, not merely hidden. The picker reads two
			    Redux slices and fetches both on mount, and this composer is used on
			    surfaces that never load either. */}
			{libraryOpen && (
				<ImageLibraryPicker
					open
					onClose={() => setLibraryOpen(false)}
					onConfirm={addLibraryImages}
					title="Add from your image library"
					description="Tap to select, then add them to this note."
				/>
			)}
		</Dialog>
	)
}

export default NoteAddCN
